import {ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {UntypedFormBuilder, UntypedFormGroup} from '@angular/forms';
import {LoadingStatus} from 'amc-common-components';
import {CTS_EDIT_MODAL_CONTROLS, CTS_EDIT_MODAL_FIELDS} from '@entities/cts-distribution/constants';
import {CtsEditModalData, CtsEditModalParams} from '@entities/cts-distribution/cts-distribution.interfaces';

/**
 * Edit Modal component.
 * @class
 */
@Component({
	selector: 'edit-modal-component',
	templateUrl: './edit-modal.component.html',
	styleUrls: ['./edit-modal.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EditModalComponent implements OnInit {
	/**
	 * Input variable for providing 'data' value to component from parent.
	 */
	@Input() data: CtsEditModalData;
	/**
	 * Input variable for providing 'edit status' value to component from parent.
	 */
	@Input() editStatus: LoadingStatus;
	/**
	 * Output variable for emitting 'closed' value from component to parent.
	 */
	@Output() closed = new EventEmitter<void>();
	/**
	 * Output variable for emitting 'save rows' value from component to parent.
	 */
	@Output() saveRows = new EventEmitter<CtsEditModalParams>();

	/** Auxiliary 'controls' variable. */
	public readonly controls = CTS_EDIT_MODAL_CONTROLS;
	/** Auxiliary 'fields' variable. */
	public readonly fields = CTS_EDIT_MODAL_FIELDS;
	/** Auxiliary 'emptyFields' variable. */
	public readonly emptyFields = {};
	/** Auxiliary 'form' variable. */
	public form: UntypedFormGroup;

	/**
	 * Creates an instance of EditModalComponent.
	 * @param {UntypedFormBuilder} fb Param for accessing to Angular From Builder.
	 */
	constructor(private fb: UntypedFormBuilder) {}

	/**
	 * Default Angular framework 'ngOnInit' Lifecycle hook.
	 * @returns {void}
	 */
	ngOnInit(): void {
		this.form = this.fb.group({
			[this.controls.CONTENT_ID]: this.fb.control(null),
			[this.controls.SEASON_CONTENTID]: this.fb.control(null),
			[this.controls.SERIES_CONTENTID]: this.fb.control(null),
			[this.controls.PACKAGE_CONTENTID]: this.fb.control(null),
			[this.controls.PACKAGE_MEDIA_CONTENTD]: this.fb.control(null),
			[this.controls.PACKAGE_ADREF_CONTENTID]: this.fb.control(null),
			[this.controls.PACKAGE_MOVIEID]: this.fb.control(null),
			[this.controls.VARIANT_ID]: this.fb.control(null),
		});
	}

	/**
	 * The method used to save the form.
	 */
	public saveForm(): void {
		/**
		 * Get control value.
		 * @param {string} control
		 */
		const getControlValue = (control: string) =>
			this.emptyFields[control] ? '' : this.form.get(control).value?.trim() || null;

		this.saveRows.emit({
			PLATFORM: this.data.platforms,
			MEDIA_ID: this.data.media,
			CONTENT_ID: getControlValue(this.controls.CONTENT_ID),
			SEASON_CONTENT_ID: getControlValue(this.controls.SEASON_CONTENTID),
			SERIES_CONTENT_ID: getControlValue(this.controls.SERIES_CONTENTID),
			PACKAGE_CONTENT_ID: getControlValue(this.controls.PACKAGE_CONTENTID),
			PACKAGE_MEDIA_CONTENT_ID: getControlValue(this.controls.PACKAGE_MEDIA_CONTENTD),
			PACKAGE_ADREF_CONTENT_ID: getControlValue(this.controls.PACKAGE_ADREF_CONTENTID),
			PACKAGE_MOVIE_ID: getControlValue(this.controls.PACKAGE_MOVIEID),
			VARIANT_ID: getControlValue(this.controls.VARIANT_ID),
		});
	}

	/**
	 * The method used to handle the checkbox click.
	 * @param {string} fieldId
	 */
	public selectEmptyCheckbox(fieldId: string): void {
		this.emptyFields[fieldId] = !this.emptyFields[fieldId];

		if (!this.emptyFields[fieldId]) {
			this.form.get(fieldId).enable();
			return;
		}

		this.form.get(fieldId).reset();
		this.form.get(fieldId).disable();
	}

	/**
	 * Is form invalid.
	 */
	public get formIsInvalid(): boolean {
		return Object.values(this.controls).every(
			(control) => !this.form.get(control).value?.trim() && !this.emptyFields[control]
		);
	}
}
